/**
 * Fonzie Engine Core - v1.0
 * Shared logic for Fonzie Car Kit Builder
 * Handles product data, variant matching, and analytics
 */

class FonzieEngine {
  constructor(config = {}) {
    this.mode = config.mode || 'companion'; // 'companion' or 'drawer'
    this.currentStep = 0;
    this.maxSteps = 4; // Color, Size, Sprayer, Finish
    
    this.selections = {
      color: null,           // Color name
      colorProduct: null,    // Full product object
      size: null,            // 'Small', 'Medium', 'Large', 'XL'
      sprayer: null,         // 'G-Force Sprayer', 'Ultimate Sprayer', 'No Sprayer'
      finish: null           // 'Standard', 'Ultra High Gloss'
    };
    
    this.carKitProducts = [];
    this.colorGroups = {
      solid: [],
      metallic: [],
      colorshift: [],
      hypershift: [],
      holographic: []
    };
    
    this.isLoading = false;
    this.isLoaded = false;
    
    // Video URLs (update these when videos are uploaded)
    this.videos = config.videos || {
      welcome: '',
      color: '',
      size: '',
      sprayer: '',
      finish: '',
      complete: ''
    };
  }
  
  /**
   * Load all car kit products from collection
   * Cache results in sessionStorage for performance
   */
  async loadCarKitProducts() {
    if (this.isLoaded) {
      console.log('✅ Fonzie Engine: Products already loaded');
      return this.carKitProducts;
    }
    
    if (this.isLoading) {
      console.log('⏳ Fonzie Engine: Load already in progress');
      return this.carKitProducts;
    }
    
    this.isLoading = true;
    
    // Try to load from cache first
    const cacheKey = 'fonzie_car_kit_products';
    const cacheExpiry = 1000 * 60 * 30; // 30 minutes
    
    try {
      const cached = sessionStorage.getItem(cacheKey);
      const cacheTime = sessionStorage.getItem(cacheKey + '_time');
      
      if (cached && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < cacheExpiry) {
          console.log('📦 Fonzie Engine: Loading from cache');
          const cachedData = JSON.parse(cached);
          this.carKitProducts = cachedData.products;
          this.colorGroups = cachedData.groups;
          this.isLoaded = true;
          this.isLoading = false;
          return this.carKitProducts;
        }
      }
    } catch (e) {
      console.warn('⚠️ Fonzie Engine: Cache read error', e);
    }
    
    // Fetch from API
    try {
      console.log('🔄 Fonzie Engine: Fetching products from API...');
      const response = await fetch('/collections/dipyourcar-peelable-car-kits/products.json?limit=250');
      
      if (!response.ok) {
        throw new Error('Failed to fetch products: ' + response.status);
      }
      
      const data = await response.json();
      this.carKitProducts = data.products || [];
      
      // Group products by color category
      this.groupProducts();
      
      // Cache the results
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          products: this.carKitProducts,
          groups: this.colorGroups
        }));
        sessionStorage.setItem(cacheKey + '_time', Date.now().toString());
      } catch (e) {
        console.warn('⚠️ Fonzie Engine: Cache write error', e);
      }
      
      console.log('✅ Fonzie Engine: Loaded', this.carKitProducts.length, 'car kit products');
      this.trackEvent('products_loaded', this.carKitProducts.length);
      
      this.isLoaded = true;
      this.isLoading = false;
      
      return this.carKitProducts;
    } catch (error) {
      console.error('❌ Fonzie Engine: Error loading products', error);
      this.isLoading = false;
      return [];
    }
  }
  
  /**
   * Group products by finish type using tags
   */
  groupProducts() {
    this.colorGroups = {
      solid: [],
      metallic: [],
      colorshift: [],
      hypershift: [],
      holographic: []
    };
    
    this.carKitProducts.forEach(product => {
      const colorData = {
        handle: product.handle,
        id: product.id,
        title: product.title
          .replace(' Peelable Car Kit', '')
          .replace(' Car Kit', '')
          .trim(),
        image: product.images && product.images[0] ? product.images[0].src : '',
        featuredImage: product.featured_image || (product.images && product.images[0] ? product.images[0].src : ''),
        variants: product.variants || [],
        tags: product.tags || [],
        productType: product.product_type || '',
        priceRange: {
          min: product.variants && product.variants.length > 0 ? 
            Math.min(...product.variants.map(v => v.price)) : 0,
          max: product.variants && product.variants.length > 0 ? 
            Math.max(...product.variants.map(v => v.price)) : 0
        }
      };
      
      // Determine category from tags
      const tags = product.tags || [];
      let category = 'solid'; // default
      
      // Check for finish type tags
      if (tags.some(tag => tag.toLowerCase() === 'holographic')) {
        category = 'holographic';
      } else if (tags.some(tag => tag.toLowerCase() === 'hypershift')) {
        category = 'hypershift';
      } else if (tags.some(tag => tag.toLowerCase().includes('colorshift'))) {
        category = 'colorshift';
      } else if (tags.some(tag => tag.toLowerCase() === 'metallic')) {
        category = 'metallic';
      } else if (tags.some(tag => tag.toLowerCase() === 'solid')) {
        category = 'solid';
      }
      
      colorData.category = category;
      
      // Add to appropriate group
      if (this.colorGroups[category]) {
        this.colorGroups[category].push(colorData);
      }
    });
    
    // Sort each group alphabetically
    Object.keys(this.colorGroups).forEach(key => {
      this.colorGroups[key].sort((a, b) => a.title.localeCompare(b.title));
    });
    
    console.log('📊 Fonzie Engine: Color groups', {
      solid: this.colorGroups.solid.length,
      metallic: this.colorGroups.metallic.length,
      colorshift: this.colorGroups.colorshift.length,
      hypershift: this.colorGroups.hypershift.length,
      holographic: this.colorGroups.holographic.length
    });
  }
  
  /**
   * Get product by handle
   */
  getProductByHandle(handle) {
    return this.carKitProducts.find(p => p.handle === handle);
  }
  
  /**
   * Find matching variant based on all selections
   * Returns the variant that matches size + sprayer + finish
   */
  findMatchingVariant() {
    if (!this.selections.colorProduct) {
      console.warn('⚠️ Fonzie Engine: No color product selected');
      return null;
    }
    
    const product = this.getProductByHandle(this.selections.colorProduct.handle);
    if (!product || !product.variants) {
      console.warn('⚠️ Fonzie Engine: Product not found or has no variants');
      return null;
    }
    
    console.log('🔍 Finding variant match for:', {
      product: product.title,
      size: this.selections.size,
      sprayer: this.selections.sprayer,
      finish: this.selections.finish
    });
    
    // Try to find exact match
    const variant = product.variants.find(v => {
      if (!v.available) return false;
      
      const variantOptions = [
        v.option1 ? v.option1.toLowerCase() : '',
        v.option2 ? v.option2.toLowerCase() : '',
        v.option3 ? v.option3.toLowerCase() : ''
      ];
      
      // Size matching (option1)
      const sizeMatch = !this.selections.size || variantOptions.some(opt => {
        const sizeLower = this.selections.size.toLowerCase();
        
        // Handle XL ↔ Extra Large mapping
        if ((sizeLower === 'xl' && opt.includes('extra large')) ||
            (sizeLower === 'extra large' && opt === 'xl')) {
          return true;
        }
        
        // Prevent "Large" from matching "Extra Large"
        if (sizeLower === 'large' && opt.includes('extra')) {
          return false;
        }
        if ((sizeLower === 'extra large' || sizeLower === 'xl') && opt === 'large') {
          return false;
        }
        
        return opt.includes(sizeLower) || sizeLower.includes(opt);
      });
      
      // Sprayer matching (option2)
      const sprayerMatch = !this.selections.sprayer || variantOptions.some(opt => {
        const sprayerLower = this.selections.sprayer.toLowerCase();
        
        // Match "No Sprayer"
        if (sprayerLower.includes('no sprayer') && (opt.includes('no') || opt.includes('have') || opt.includes('already'))) {
          return true;
        }
        
        // Match "G-Force Sprayer"
        if (sprayerLower.includes('g-force') && (opt.includes('g-force') || opt.includes('gforce') || (opt.includes('yes') && !opt.includes('ultimate')))) {
          return true;
        }
        
        // Match "Ultimate Sprayer"
        if (sprayerLower.includes('ultimate') && opt.includes('ultimate')) {
          return true;
        }
        
        return false;
      });
      
      // Finish matching (option3)
      const finishMatch = !this.selections.finish || variantOptions.some(opt => {
        const finishLower = this.selections.finish.toLowerCase();
        
        if (finishLower.includes('standard') && (opt.includes('standard') || opt.includes('matte') || opt.includes('satin'))) {
          return true;
        }
        
        if (finishLower.includes('ultra') || finishLower.includes('gloss') || finishLower.includes('uhg')) {
          return opt.includes('ultra') || opt.includes('gloss') || opt.includes('uhg');
        }
        
        return false;
      });
      
      return sizeMatch && sprayerMatch && finishMatch;
    });
    
    if (variant) {
      console.log('✅ Found matching variant:', variant.id, variant.title);
      return variant;
    }
    
    // Fallback: try partial matches with scoring
    console.warn('⚠️ No exact match, trying partial match...');
    let bestMatch = null;
    let bestScore = 0;
    
    for (const v of product.variants) {
      if (!v.available) continue;
      
      let score = 0;
      const variantOptions = [
        v.option1 ? v.option1.toLowerCase() : '',
        v.option2 ? v.option2.toLowerCase() : '',
        v.option3 ? v.option3.toLowerCase() : ''
      ];
      
      // Size is most important (3 points)
      if (this.selections.size) {
        const sizeLower = this.selections.size.toLowerCase();
        const sizeMatch = variantOptions.some(opt => {
          if ((sizeLower === 'xl' && opt.includes('extra large')) ||
              (sizeLower === 'extra large' && opt === 'xl')) return true;
          if (sizeLower === 'large' && opt.includes('extra')) return false;
          return opt.includes(sizeLower);
        });
        if (sizeMatch) score += 3;
      }
      
      // Finish is second most important (2 points)
      if (this.selections.finish) {
        const finishLower = this.selections.finish.toLowerCase();
        const finishMatch = variantOptions.some(opt => {
          if (finishLower.includes('standard') && (opt.includes('standard') || opt.includes('matte'))) return true;
          if (finishLower.includes('gloss') && opt.includes('gloss')) return true;
          return false;
        });
        if (finishMatch) score += 2;
      }
      
      // Sprayer is least important (1 point)
      if (this.selections.sprayer) {
        const sprayerLower = this.selections.sprayer.toLowerCase();
        const sprayerMatch = variantOptions.some(opt => {
          if (sprayerLower.includes('no') && opt.includes('no')) return true;
          if (sprayerLower.includes('g-force') && opt.includes('g-force')) return true;
          if (sprayerLower.includes('ultimate') && opt.includes('ultimate')) return true;
          return false;
        });
        if (sprayerMatch) score += 1;
      }
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = v;
      }
    }
    
    if (bestMatch) {
      console.log('✅ Found partial match variant:', bestMatch.id, 'score:', bestScore);
      return bestMatch;
    }
    
    // Last resort: return first available variant
    const fallback = product.variants.find(v => v.available);
    console.warn('⚠️ Using fallback variant:', fallback ? fallback.id : 'none');
    return fallback || null;
  }
  
  /**
   * Calculate kit price
   */
  getKitPrice() {
    const variant = this.findMatchingVariant();
    if (!variant || !variant.price) return '0.00';
    return (variant.price / 100).toFixed(2);
  }
  
  /**
   * Get formatted summary of selections
   */
  getSummary() {
    return {
      color: this.selections.colorProduct?.title || 'Not selected',
      size: this.selections.size || 'Not selected',
      sprayer: this.selections.sprayer || 'Not selected',
      finish: this.selections.finish || 'Not selected',
      price: '$' + this.getKitPrice(),
      variant: this.findMatchingVariant()
    };
  }
  
  /**
   * Reset all selections
   */
  reset() {
    this.currentStep = 0;
    this.selections = {
      color: null,
      colorProduct: null,
      size: null,
      sprayer: null,
      finish: null
    };
    this.trackEvent('builder_reset', 'user_initiated');
  }
  
  /**
   * Track analytics events
   */
  trackEvent(action, value, metadata = {}) {
    if (!window.DYCAnalytics) {
      console.log('📊 Analytics not available (would track):', action, value);
      return;
    }
    
    const trackingData = {
      builderType: this.mode === 'drawer' ? 'fonzie_drawer' : 'fonzie_companion',
      currentStep: this.currentStep,
      selections: JSON.stringify(this.selections),
      timestamp: new Date().toISOString(),
      ...metadata
    };
    
    try {
      window.DYCAnalytics.trackCustom('fonzie_car_kit_builder', action, value, trackingData);
      console.log('📊 Fonzie tracked:', action, value);
    } catch (error) {
      console.error('❌ Tracking error:', error);
    }
  }
  
  /**
   * Format price for display
   */
  formatPrice(priceInCents) {
    return '$' + (priceInCents / 100).toFixed(2);
  }
}

// Export for use in other scripts
window.FonzieEngine = FonzieEngine;

console.log('✅ Fonzie Engine Core loaded');


